import { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import {
  Download, ArrowRight, FileText, Image as ImageIcon,
  Film, Package, Newspaper, ExternalLink,
} from "lucide-react";

const CAT_META = {
  press_release: { label: "Press Release", icon: Newspaper },
  brand_asset:   { label: "Brand Asset",   icon: FileText },
  photo:         { label: "Photo",         icon: ImageIcon },
  video:         { label: "Video",         icon: Film },
  logo_pack:     { label: "Logo Pack",     icon: Package },
  other:         { label: "Resource",      icon: FileText },
};

function formatSize(bytes) {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function MediaCenter() {
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    full_name: "", outlet: "", role: "", email: "", phone: "",
    coverage_type: "Press / Editorial", portfolio_url: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/media").then((r) => setAssets(r.data)).catch(() => {});
  }, []);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/media/credentials", form);
      toast.success("Media request received. Credentials decision within 5 business days.");
      setForm({ ...form, full_name: "", outlet: "", role: "", email: "", phone: "", portfolio_url: "", message: "" });
    } catch {
      toast.error("Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadAsset = (a) => {
    const url = `${API}/media/${a.id}/download`;
    // Backend returns 307 redirect for external links and `Content-Disposition: attachment`
    // for stored files. Opening in a new tab works cleanly for both.
    window.open(url, "_blank", "noopener,noreferrer");
    // Optimistically bump the local counter.
    setAssets((prev) => prev.map((m) => m.id === a.id ? { ...m, download_count: (m.download_count || 0) + 1 } : m));
  };

  const categories = Array.from(new Set(assets.map((a) => a.category)));
  const filtered = filter === "all" ? assets : assets.filter((a) => a.category === filter);

  return (
    <section
      id="media"
      data-testid="media-room-section"
      className="relative py-24 md:py-32 bg-aawta-charcoal scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 mb-10 items-end">
          <div className="md:col-span-7">
            <p className="text-overline text-aawta-pink mb-4">MEDIA ROOM</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
              Press, podcasters,<br /> creators &mdash; <em className="italic text-aawta-pink">welcome.</em>
            </h2>
          </div>
          <p className="md:col-span-5 text-white/60 leading-relaxed">
            Red-carpet press access, interview booking, podcast features, and content
            creation rights. Download approved assets below or apply for credentials.
          </p>
        </div>

        {/* Category filter */}
        {assets.length > 0 && categories.length > 1 && (
          <div data-testid="media-filter-bar" className="flex flex-wrap gap-2 mb-6">
            <FilterPill label="All" active={filter === "all"} onClick={() => setFilter("all")} testId="media-filter-all" />
            {categories.map((c) => (
              <FilterPill
                key={c}
                label={CAT_META[c]?.label || c}
                active={filter === c}
                onClick={() => setFilter(c)}
                testId={`media-filter-${c}`}
              />
            ))}
          </div>
        )}

        {/* Asset grid */}
        {assets.length === 0 ? (
          <div data-testid="media-empty" className="border border-dashed border-white/15 p-12 text-center text-white/40 mb-14">
            Media assets will appear here once published by the AAWTA team.
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3 mb-14" data-testid="media-asset-grid">
            {filtered.map((a) => {
              const meta = CAT_META[a.category] || CAT_META.other;
              const Icon = meta.icon;
              const size = formatSize(a.size);
              const isLink = !!a.external_url;
              return (
                <button
                  key={a.id}
                  data-testid={`media-asset-${a.id}`}
                  onClick={() => downloadAsset(a)}
                  className="text-left border border-white/10 p-6 bg-aawta-jet hover:border-aawta-pink/40 hover:bg-white/5 transition-all group flex flex-col"
                >
                  <div className="flex items-start justify-between mb-6">
                    <Icon className="text-aawta-pink" size={22} />
                    {isLink ? (
                      <ExternalLink className="text-white/40 group-hover:text-white transition-colors" size={16} />
                    ) : (
                      <Download className="text-white/40 group-hover:text-white transition-colors" size={16} />
                    )}
                  </div>
                  <p className="font-display text-lg text-white">{a.title}</p>
                  <p className="text-overline text-white/40 mt-2">
                    {meta.label}
                    {size && <> · {size}</>}
                    {isLink && !size && <> · External link</>}
                  </p>
                  {a.description && (
                    <p className="text-xs text-white/55 mt-3 line-clamp-2">{a.description}</p>
                  )}
                  {a.download_count > 0 && (
                    <p className="text-[10px] tracking-[0.2em] text-white/30 mt-4">
                      {a.download_count} DOWNLOAD{a.download_count === 1 ? "" : "S"}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Press credentials */}
        <form onSubmit={submit} data-testid="media-form" className="glass-panel p-8 md:p-10 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-12">
            <p className="text-overline text-aawta-pink">PRESS CREDENTIALS · INTERVIEW REQUEST</p>
            <h4 className="font-display text-2xl md:text-3xl text-white mt-2">Apply for media access.</h4>
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Name *</label>
            <input data-testid="med-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Outlet / Publication *</label>
            <input data-testid="med-outlet" required className="input-luxe" value={form.outlet} onChange={handle("outlet")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Role *</label>
            <input data-testid="med-role" required placeholder="Reporter, Producer..." className="input-luxe" value={form.role} onChange={handle("role")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Email *</label>
            <input data-testid="med-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Phone</label>
            <input data-testid="med-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Coverage Type *</label>
            <select data-testid="med-coverage" required className="input-luxe bg-aawta-jet" value={form.coverage_type} onChange={handle("coverage_type")}>
              <option>Press / Editorial</option>
              <option>Podcast Feature</option>
              <option>Photo / Video</option>
              <option>Live Broadcast</option>
              <option>Influencer / Social</option>
              <option>Interview Request</option>
            </select>
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Portfolio URL</label>
            <input data-testid="med-portfolio" className="input-luxe" value={form.portfolio_url} onChange={handle("portfolio_url")} />
          </div>
          <div className="md:col-span-12">
            <label className="label-luxe">Pitch / Notes</label>
            <textarea data-testid="med-message" rows={3} className="input-luxe resize-none" value={form.message} onChange={handle("message")} />
          </div>
          <div className="md:col-span-12">
            <button type="submit" data-testid="med-submit" disabled={submitting} className="btn-pink disabled:opacity-50">
              {submitting ? "SUBMITTING..." : "REQUEST CREDENTIALS"} <ArrowRight size={14} />
            </button>
            <p className="text-overline text-white/40 mt-3">PRESS CONTACT · contact@aawta.org · 770-756-6768</p>
          </div>
        </form>
      </div>
    </section>
  );
}

function FilterPill({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-overline border transition-all ${
        active
          ? "border-aawta-pink text-aawta-pink bg-aawta-pink/10"
          : "border-white/15 text-white/60 hover:border-white/30 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}
